<template>
  <div class="action-menu-item" @click="exportDocx">
    Сформировать отчет
  </div>
</template>

<script>
import { DATE_FORMAT } from '@/main.config.js'
import { eventsConfig, notShow } from '@/events.config.js'

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts.js'
import { patientMaxHR } from '@/mixins/patient-max-hr'
import { fullName } from '@/mixins/full-name.js'
import timeString from '@/modules/time-string.js'
import { trainingDate } from '@/mixins/training-date.js'
import getAverageOfArray from '@/modules/average-of-array.js'
import { userRights } from '@/mixins/user-rights.js'

export default {
  name: 'ProtocolSession',
  mixins: [patientMaxHR, fullName, trainingDate, userRights],
  props: {
    training: {
      type: Object,
      required: true
    },
    patient: {
      type: Object,
      required: true
    },
    stepRate: {
      type: Object,
      required: false
    },
    rhythmogram: {
      type: Object,
      required: false
    },
    events: {
      type: Array,
      required: false
    }
  },
  data: () => ({
    eventsList: null
  }),
  computed: {
    fullNamePatient() {
      return this.getFullName(this.patient)
    },
    gender() {
      if (this.patient.gender === 0) {
        return 'Муж'
      } else if (this.patient.gender === 1) {
        return 'Жен'
      } else return '—'
    },
    height() {
      if (this.patient.height) return `${this.patient.height} см`
      else return '—'
    },
    weight() {
      if (this.patient.weight) return `${this.patient.weight} кг`
      else return '—'
    },
    datePlan() {
      return this.moment(this.training.date_plan).format(DATE_FORMAT.ru)
    },
    intervalHR() {
      if (this.training.heart_rate_min && this.training.heart_rate_max) {
        return `${this.training.heart_rate_min} - ${this.training.heart_rate_max} уд/мин`
      } else return '—'
    },
    startTime() {
      let time = this.moment.utc(this.training.date_start, DATE_FORMAT.serverFull).local()
      return time.format(DATE_FORMAT.timeHMS)
    },
    endTime() {
      let time = this.moment.utc(this.training.date_end, DATE_FORMAT.serverFull).local()
      return time.format(DATE_FORMAT.timeHMS)
    },
    adjustedDurationStr() {
      return !this.training.duration || this.training.duration === 0 ? '—' : `${this.training.duration} мин`
    },
    duration() {
      return this.getDoneTrainingDuration(this.training)
    },
    diagnosisStr() {
      if (this.training.diagnosis) return this.training.diagnosis
      else return '–'
    },
    trainingDurationString() {
      if (this.duration === 0) {
        return '–'
      }
      return timeString(this.duration)
    },
    hasStepRate() {
      return (
        !!this.stepRate &&
        typeof this.stepRate.rateValues !== 'undefined' &&
        this.stepRate.rateValues.length > 0
      )
    },
    averageStepRate() {
      if (!this.hasStepRate) {
        return '–'
      }
      return `${getAverageOfArray(this.stepRate.rateValues)} шаг/мин`
    },
    stepCountStr() {
      if (!this.training.step_count) {
        return '-'
      } else return this.training.step_count
    },
    borgScaleStr() {
      if (!this.training.borg_scale) {
        return '-'
      } else return this.training.borg_scale
    },
    hasRhythmogram() {
      return (
        !!this.rhythmogram &&
        typeof this.rhythmogram.hrValues !== 'undefined' &&
        this.rhythmogram.hrValues.length > 0
      )
    },
    maxHeartRate() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      return Math.max(...this.rhythmogram.hrValues)
    },
    averageHeartRate() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      return getAverageOfArray(this.rhythmogram.hrValues)
    },
    hrInZonePercent() {
      if (!this.hasRhythmogram) {
        return '–'
      }
      if (!this.training.heart_rate_max || !this.training.heart_rate_min) {
        return 100
      }
      const fullCount = this.rhythmogram.hrValues.length
      const zoneCount = this.rhythmogram.hrValues.reduce((count, value) => {
        if (value <= this.training.heart_rate_max && value >= this.training.heart_rate_min) {
          count++
        }
        return count
      }, 0)
      return Math.round((zoneCount / fullCount) * 100)
    },
    patientCommentStr() {
      let text = this.events.filter(ev => ev.type === 'eventText')
      let strResult = ''
      text.forEach(ev => {
        let timeBegin = timeString(ev.begin, true)
        strResult += `${timeBegin} - ${ev.value}\n`
      })
      if (strResult !== '') strResult += '\n'
      if (this.training.comment) {
        strResult += `${this.training.comment}`
      }
      if (strResult !== '') return strResult
      else return 'Жалоб нет'
    },
    basicRhythm() {
      if (this.training.basic_rhythm) return this.training.basic_rhythm
      else return '–'
    },
    exercise() {
      return this.training.description ? this.training.description : '-'
    },
    doctorCommentStr() {
      if (this.training.specialist_interpretation) return this.training.specialist_interpretation
      else return '–'
    },
    conclusionStr() {
      let str = ''
      if (this.training.basic_rhythm) str += `основной ритм ${this.basicRhythm}, `
      if (this.hasRhythmogram) str += `средняя ЧСС ${this.averageHeartRate} уд/мин, `
      if (this.hasRhythmogram) str += `максимальная ЧСС ${this.maxHeartRate} уд/мин, `
      if (str !== '') {
        str = str.replace(/,(?=[^,]+$)/, '.')
      }
      if (this.training.specialist_interpretation)
        str += `\nИнтерпретация специалиста: ${this.doctorCommentStr}.`
      return str
    },
    tableForRegistredEvents() {
      let firstColumn = [
        [{ text: 'Событие', style: 'tableHeader' }, { text: 'Количество', style: 'tableHeader' }]
      ]
      let secondColumn = [
        [{ text: 'Событие', style: 'tableHeader' }, { text: 'Количество', style: 'tableHeader' }]
      ]
      if (this.eventsList.length >= 10) {
        let halfLengthEventsList = Math.round(this.eventsList.length / 2)
        for (let i = 0; i < halfLengthEventsList; i++) {
          let row = new Array()
          row.push({ text: `${this.eventsList[i].text}` })
          row.push({ text: `${this.eventsList[i].count}`, alignment: 'center' })
          firstColumn.push(row)
        }
        for (let i = halfLengthEventsList; i < this.eventsList.length; i++) {
          let row = new Array()
          row.push({ text: `${this.eventsList[i].text}` })
          row.push({ text: `${this.eventsList[i].count}`, alignment: 'center' })
          secondColumn.push(row)
        }
      } else {
        this.eventsList.forEach(el => {
          let row = new Array()
          row.push({ text: `${el.text}` })
          row.push({ text: `${el.count}`, alignment: 'center' })
          firstColumn.push(row)
        })
      }
      return { firstColumn, secondColumn }
    }
  },
  methods: {
    async exportDocx() {
      this.addEventsToList()
      pdfMake.vfs = pdfFonts.pdfMake.vfs
      const logoNSRus = `<svg xmlns="http://www.w3.org/2000/svg" width="97" height="20" viewBox="0 0 97 20" fill="none">
                          <g clip-path="url(#clip0_7_8)">
                            <path d="M8.92754 13.4443C8.01868 13.4443 7.28377 12.6839 7.28377 11.7435C7.28377 10.8031 8.01868 10.0427 8.92754 10.0427C9.8364 10.0427 10.5713 10.8031 10.5713 11.7435C10.5713 12.6839 9.8364 13.4443 8.92754 13.4443ZM17.7639 11.604C17.7639 11.604 17.1681 11.3521 16.9768 11.2441C15.0286 10.1642 13.9415 8.41393 13.9589 6.34419C13.9589 6.2767 13.9589 6.20921 13.9589 6.14172C13.9589 6.07423 13.9632 6.00673 13.9676 5.93924C13.9676 5.87175 13.9763 5.79976 13.9806 5.73227C13.985 5.66478 13.9937 5.59279 13.998 5.52529C14.0067 5.4578 14.0111 5.38581 14.0198 5.31832C14.0285 5.25083 14.0372 5.17884 14.0458 5.11135C14.0545 5.04386 14.0632 4.97186 14.0763 4.90437C14.085 4.83688 14.098 4.76939 14.1111 4.6974C14.1241 4.62991 14.1372 4.56242 14.1502 4.49492C14.1633 4.42743 14.1763 4.35994 14.1894 4.29245C14.2024 4.22496 14.2198 4.15747 14.2328 4.08997C14.2459 4.02248 14.2633 3.95499 14.2763 3.892C14.2937 3.82451 14.3068 3.76152 14.3242 3.69402C14.3416 3.63103 14.3589 3.56354 14.372 3.50055C14.3894 3.43756 14.4068 3.37456 14.4242 3.30707C14.4416 3.24408 14.459 3.18109 14.4764 3.1181C14.4938 3.0551 14.5111 2.99211 14.5285 2.93362C14.5459 2.87513 14.5633 2.81213 14.5807 2.75364C14.5981 2.69515 14.6155 2.63666 14.6373 2.57816C14.6547 2.51967 14.6764 2.46118 14.6938 2.40718C14.7112 2.34869 14.7329 2.2947 14.7503 2.23621C14.7677 2.18221 14.7895 2.12822 14.8069 2.07423C14.8242 2.02023 14.846 1.97074 14.8634 1.91675C14.8808 1.86725 14.8982 1.81326 14.9199 1.76377C14.9373 1.71427 14.9547 1.66478 14.9721 1.61528C14.9895 1.56579 15.0069 1.5208 15.0243 1.4758C15.0417 1.43081 15.0591 1.38581 15.0765 1.34082C15.0939 1.30032 15.1113 1.25533 15.1243 1.21483C15.1417 1.17434 15.1547 1.13384 15.1721 1.09335C15.1852 1.05735 15.2026 1.01686 15.2156 0.980863C15.2287 0.944867 15.2461 0.908872 15.2591 0.872877C15.2722 0.84138 15.2852 0.805385 15.2982 0.773889C15.3113 0.742393 15.3243 0.715396 15.3374 0.6839C15.3504 0.656903 15.3591 0.629907 15.3722 0.60291C15.3809 0.580413 15.3939 0.553416 15.4026 0.530919C15.4113 0.508422 15.42 0.490424 15.4287 0.467927C15.4374 0.449929 15.4461 0.431932 15.4504 0.413934C15.4765 0.355441 15.507 0.292449 15.5244 0.229457V0.215959C15.5418 0.170964 15.5374 0.116971 15.5113 0.076476C15.4896 0.0404805 15.4548 0.00898442 15.4113 -1.44553e-05C15.3678 -0.0135128 15.32 -0.00901333 15.2852 0.0179833C15.2461 0.0404805 15.22 0.0809754 15.1895 0.116971C15.1591 0.157466 15.133 0.197961 15.1069 0.238456C15.0765 0.28795 15.0417 0.332944 15.0112 0.382438C14.9678 0.44543 14.9243 0.512921 14.8764 0.575914C14.8199 0.652404 14.7634 0.733394 14.7068 0.809884C14.6416 0.899873 14.572 0.989862 14.5068 1.07985C14.4285 1.17884 14.3546 1.28233 14.2763 1.38131C14.1894 1.4893 14.1024 1.59729 14.0154 1.70527C13.9197 1.81776 13.8241 1.93474 13.7284 2.04723C13.624 2.16422 13.5197 2.2812 13.4153 2.39819C13.3066 2.51967 13.1935 2.63666 13.0805 2.75364C12.963 2.87513 12.8456 2.99211 12.7239 3.1091C12.6021 3.22608 12.476 3.34307 12.3499 3.45555C12.2238 3.56804 12.089 3.68053 11.9585 3.78851C11.8237 3.8965 11.6889 3.99999 11.5497 4.10347C11.4193 4.19796 11.2845 4.29245 11.1453 4.38244C9.96685 5.14284 9.19715 5.22383 8.90145 5.25533C7.14026 5.4533 5.68783 4.53542 5.3008 4.28345C5.00075 4.08997 4.76592 3.901 4.60937 3.76152C4.50501 3.68053 4.3615 3.77051 4.37455 3.901C4.37455 3.901 4.52675 5.15184 4.54849 5.57029C4.60068 6.47918 4.55719 7.57704 3.9049 8.74689C3.13954 10.1282 1.8741 10.7671 1.561 10.9291C1.13484 11.1451 0.073777 11.6085 0.073777 11.6085C-0.0436353 11.6625 -0.0436353 11.838 0.073777 11.892C0.073777 11.892 1.19572 12.3869 1.561 12.5714C2.44377 13.0124 3.24391 13.5703 3.90055 14.7401C4.67025 16.117 4.55284 17.5118 4.54849 17.9302C4.54849 18.4207 4.37455 19.5995 4.37455 19.5995C4.3615 19.7345 4.50501 19.82 4.60937 19.739C4.60937 19.739 5.58346 18.9831 5.9183 18.7446C6.61843 18.2542 7.58382 17.7413 8.88405 17.7323C10.4322 17.7233 11.6106 18.5377 11.8976 18.7446C12.2325 18.9876 13.2066 19.739 13.2066 19.739C13.3109 19.82 13.4544 19.73 13.4414 19.5995C13.4414 19.5995 13.2848 18.3487 13.2674 17.9302C13.224 16.9404 13.2587 15.919 13.9197 14.7401C14.6894 13.3678 15.9462 12.7289 16.2593 12.5714C16.6811 12.3554 17.7465 11.892 17.7465 11.892C17.8639 11.838 17.8639 11.6625 17.7465 11.6085L17.7639 11.604Z" fill="#0AADF9"/>
                            <path d="M43.4905 6.67715C43.4818 7.0596 43.1818 7.37006 42.8078 7.37006C42.4338 7.37006 42.1338 7.0596 42.1294 6.67715H40.9379C40.9466 7.74352 41.7815 8.60291 42.8122 8.60291C43.8428 8.60291 44.6777 7.74352 44.6864 6.67715H43.4949H43.4905ZM75.5528 15.2216C76.1399 15.2216 76.5791 15.0146 76.8661 14.6052C77.1574 14.1957 77.3009 13.6693 77.3009 13.0214C77.3009 12.3734 77.1531 11.82 76.8617 11.4196C76.566 11.0191 76.1312 10.8211 75.5528 10.8211C75.1527 10.8211 74.8266 10.9156 74.57 11.1001C74.3134 11.2846 74.1265 11.5455 74.0003 11.874C73.8786 12.2025 73.8134 12.5849 73.8134 13.0214C73.8134 13.6918 73.9612 14.2272 74.2526 14.6232C74.5439 15.0191 74.9788 15.2171 75.5485 15.2171L75.5528 15.2216ZM75.5528 16.9899C74.8222 16.9899 74.183 16.8189 73.6307 16.4814C73.0784 16.144 72.6523 15.676 72.3435 15.0821C72.0348 14.4882 71.8826 13.7998 71.8826 13.0259C71.8826 12.252 72.0391 11.5545 72.3522 10.9561C72.6653 10.3622 73.1002 9.89425 73.6481 9.56129C74.2004 9.22833 74.8353 9.06185 75.5528 9.06185C76.2703 9.06185 76.927 9.23283 77.4792 9.57029C78.0315 9.90775 78.462 10.3757 78.7751 10.9741C79.0839 11.568 79.2404 12.2565 79.2404 13.0304C79.2404 13.8043 79.0839 14.4972 78.7751 15.0911C78.462 15.685 78.0315 16.153 77.4749 16.4904C76.9226 16.8279 76.2834 16.9944 75.5572 16.9944L75.5528 16.9899ZM59.4891 15.2216C60.0761 15.2216 60.5153 15.0146 60.8023 14.6052C61.0937 14.1957 61.2372 13.6693 61.2372 13.0214C61.2372 12.3734 61.0893 11.82 60.798 11.4196C60.5066 11.0191 60.0674 10.8211 59.4891 10.8211C59.089 10.8211 58.7628 10.9156 58.5063 11.1001C58.2497 11.2846 58.0627 11.5455 57.9366 11.874C57.8148 12.2025 57.754 12.5849 57.754 13.0214C57.754 13.6918 57.9018 14.2272 58.1932 14.6232C58.4889 15.0191 58.9194 15.2171 59.4891 15.2171V15.2216ZM59.4891 16.9899C58.7585 16.9899 58.1192 16.8189 57.567 16.4814C57.0147 16.144 56.5885 15.676 56.2798 15.0821C55.971 14.4882 55.8188 13.7998 55.8188 13.0259C55.8188 12.252 55.9754 11.5545 56.2885 10.9561C56.6016 10.3622 57.0364 9.89425 57.5844 9.56129C58.1366 9.22833 58.7715 9.06185 59.4891 9.06185C60.2066 9.06185 60.8632 9.23283 61.4155 9.57029C61.9678 9.90775 62.4026 10.3757 62.7114 10.9741C63.0201 11.568 63.1767 12.2565 63.1767 13.0304C63.1767 13.8043 63.0201 14.4972 62.7114 15.0911C62.3983 15.685 61.9678 16.153 61.4155 16.4904C60.8632 16.8279 60.224 16.9944 59.4978 16.9944L59.4891 16.9899ZM67.6688 16.9899C66.9947 16.9899 66.3859 16.8459 65.8424 16.5534C65.2988 16.261 64.8683 15.829 64.5508 15.2486C64.2334 14.6727 64.0725 13.9527 64.0725 13.0888V12.8009C64.0725 11.9775 64.2377 11.2891 64.5682 10.7356C64.8987 10.1822 65.3379 9.75927 65.8815 9.4758C66.4251 9.18784 67.0208 9.04386 67.6644 9.04386C68.3559 9.04386 68.9386 9.16534 69.4213 9.40381C69.8996 9.64228 70.2736 9.97074 70.5345 10.3937C70.7954 10.8166 70.9476 11.2801 70.9824 11.784H69.1299C69.056 11.4421 68.9081 11.1631 68.682 10.9606C68.4559 10.7536 68.1167 10.6547 67.6644 10.6547C67.3513 10.6547 67.0643 10.7311 66.7991 10.8841C66.5381 11.0371 66.3251 11.2711 66.1642 11.586C66.0033 11.901 65.9206 12.3059 65.9206 12.8054V13.0933C65.9206 13.6288 65.9946 14.0652 66.1468 14.4072C66.299 14.7446 66.5077 14.9966 66.7686 15.1541C67.0295 15.3116 67.3296 15.3926 67.6601 15.3926C68.021 15.3926 68.3298 15.2936 68.595 15.1001C68.856 14.9021 69.0342 14.6232 69.1256 14.2632H70.9781C70.9215 14.8076 70.7476 15.2891 70.4519 15.712C70.1562 16.135 69.7692 16.4544 69.2821 16.6749C68.7994 16.8954 68.2558 17.0034 67.6557 17.0034L67.6688 16.9899ZM34.5802 10.5917C34.2932 10.5917 34.0193 10.6412 33.7497 10.7356C33.4801 10.8301 33.2713 11.0011 33.1148 11.2441C32.9582 11.487 32.8712 11.811 32.8539 12.2115H36.2719C36.2632 11.7795 36.1805 11.4466 36.0283 11.2126C35.8761 10.9786 35.6761 10.8166 35.4282 10.7266C35.1804 10.6367 34.8977 10.5917 34.5846 10.5917H34.5802ZM34.6498 16.9899C33.9845 16.9899 33.3757 16.8504 32.8191 16.5669C32.2624 16.2835 31.8145 15.856 31.484 15.2756C31.1535 14.6996 30.9883 13.9662 30.9883 13.0888V12.8009C30.9883 11.9595 31.1448 11.2621 31.4666 10.7086C31.7841 10.1552 32.2103 9.73677 32.7451 9.4623C33.28 9.18334 33.8714 9.04386 34.515 9.04386C35.6674 9.04386 36.5415 9.35882 37.1416 9.99324C37.7417 10.6232 38.0417 11.5365 38.0417 12.7334V13.5388H32.8539C32.8712 13.9887 32.9669 14.3532 33.1365 14.6367C33.3061 14.9201 33.5279 15.1226 33.7931 15.2531C34.0628 15.3836 34.3454 15.4466 34.6498 15.4466C35.1195 15.4466 35.4761 15.3746 35.7152 15.2306C35.9544 15.0866 36.124 14.8751 36.2284 14.6007H37.9026C37.7547 15.3971 37.3894 15.991 36.8111 16.3959C36.2284 16.7964 35.5108 16.9989 34.6542 16.9989L34.6498 16.9899ZM92.6733 16.7919V10.9741H90.225V9.25533H97.0088V10.9741H94.5606V16.7919H92.6776H92.6733ZM85.7024 15.2396C86.059 15.3026 86.3634 15.2891 86.6156 15.2036C86.8679 15.1181 87.0766 14.9741 87.2332 14.7716C87.3941 14.5692 87.5071 14.3172 87.581 14.0202C87.655 13.7188 87.6898 13.3858 87.6898 13.0124C87.6898 12.6389 87.655 12.3014 87.5854 12C87.5158 11.703 87.4071 11.4511 87.2549 11.2486C87.1027 11.0461 86.8983 10.9066 86.6417 10.8211C86.3852 10.7401 86.0721 10.7311 85.6981 10.7986V15.2351L85.7024 15.2396ZM84.0413 15.2396V10.8031C83.6673 10.7356 83.3542 10.7491 83.0976 10.8346C82.8411 10.9201 82.6367 11.0686 82.4845 11.2711C82.3323 11.4736 82.2236 11.7255 82.154 12.0225C82.0844 12.3194 82.0496 12.6479 82.0496 13.0124C82.0496 13.3768 82.0844 13.7053 82.1583 14.0022C82.2323 14.2992 82.3497 14.5512 82.5062 14.7581C82.6671 14.9651 82.8715 15.1091 83.1237 15.1991C83.3759 15.2846 83.6803 15.2981 84.0369 15.2396H84.0413ZM84.0413 19.9865V16.9449C83.3107 17.0619 82.6541 16.9854 82.0627 16.7109C81.4713 16.4364 81.006 15.9865 80.6581 15.3611C80.3102 14.7356 80.1406 13.9572 80.1406 13.0169C80.1406 12.0765 80.3145 11.2936 80.6581 10.6682C81.006 10.0427 81.4713 9.59279 82.0627 9.31832C82.6541 9.04835 83.3107 8.96736 84.0413 9.08435V6.67715L85.7024 6.66815V9.08435C86.433 8.96736 87.0897 9.04385 87.6811 9.31832C88.2725 9.59279 88.7378 10.0427 89.0857 10.6682C89.4335 11.2936 89.6031 12.0765 89.6031 13.0169C89.6031 13.9572 89.4292 14.7356 89.0857 15.3611C88.7378 15.9865 88.2725 16.4364 87.6811 16.7109C87.0897 16.9854 86.4287 17.0619 85.7024 16.9449V19.9865H84.0413ZM52.74 14.3082C52.6139 14.6637 52.4226 14.9471 52.166 15.1586C51.9094 15.3701 51.5746 15.4736 51.1702 15.4736C50.7658 15.4736 50.4135 15.3746 50.1613 15.1811C49.9091 14.9876 49.7308 14.7131 49.6177 14.3577C49.509 14.0022 49.4525 13.5928 49.4525 13.1248C49.4525 12.6569 49.509 12.2475 49.6177 11.892C49.7308 11.5365 49.9047 11.2621 50.1483 11.0686C50.3918 10.8706 50.7092 10.7761 51.1006 10.7761C51.5181 10.7761 51.866 10.8841 52.1356 11.0956C52.4052 11.3071 52.6052 11.5905 52.7357 11.946C52.8661 12.3014 52.9314 12.6929 52.9314 13.1248C52.9314 13.5568 52.8705 13.9527 52.7444 14.3082H52.74ZM54.4795 10.9696C54.1881 10.3712 53.775 9.90325 53.2445 9.55679C52.7139 9.21483 52.0921 9.03936 51.3746 9.03936C50.657 9.03936 50.0656 9.21483 49.5829 9.56129C49.5351 9.59729 49.496 9.63328 49.4525 9.66928V9.25083H47.7913V19.9955H49.6873V16.5084C50.1743 16.8234 50.7658 16.9854 51.4746 16.9854C52.1834 16.9854 52.7661 16.8099 53.2792 16.4634C53.7967 16.117 54.1968 15.64 54.4882 15.0416C54.7752 14.4432 54.9187 13.7638 54.9187 13.0124C54.9187 12.261 54.7708 11.5635 54.4795 10.9651V10.9696ZM46.1388 9.25533V16.7919H44.3385V12.189L40.9597 16.7919H39.4637V9.25533H41.2641V13.7053L44.656 9.25533H46.1388ZM21.33 16.7919V6.67715H23.213V10.8346H27.6703V6.67715H29.5533V16.7919H27.6703V12.6209H23.213V16.7919H21.33Z" fill="#1C2C39"/>
                          </g>
                          <defs><clipPath id="clip0_7_8"><rect width="97" height="20" fill="white"/></clipPath></defs>
                        </svg>`
      let colorGreen = '#D7F1E7'
      let docDefinition = {
        pageMargins: [40, 100, 40, 60],
        header: {
          margin: [40, 40, 40, 40],
          stack: [
            {
              alignment: 'justify',
              columns: [
                {
                  width: 250,
                  style: 'header',
                  text: [`ОТЧЕТ О ПРОХОЖДЕНИИ\n СЕССИИ ${this.training.id}`]
                },
                {
                  width: 130,
                  text: [
                    {
                      style: 'bold',
                      text: `Пациент: \n`
                    },
                    {
                      text: `${this.fullNamePatient}`
                    }
                  ]
                },
                {
                  stack: [
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Пол: `
                        },
                        {
                          text: `${this.gender}\n\n`
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Возраст: `
                        },
                        {
                          text: `${this.age}\n`
                        }
                      ]
                    }
                  ]
                },

                {
                  stack: [
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Рост: `
                        },
                        {
                          text: `${this.height}\n\n`
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          style: 'bold',
                          text: `Вес: `
                        },
                        {
                          text: `${this.weight}\n`
                        }
                      ]
                    }
                  ]
                }
              ]
            },
            `\n`,
            {
              canvas: [
                {
                  lineColor: '#5F5F61',
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 515,
                  y2: 0,
                  lineWidth: 2
                }
              ]
            },
            `\n`
          ]
        },
        footer: {
          columns: [
            {
              svg: logoNSRus,
              margin: [40, 15, 0, 0]
            },
            {
              text: 'Аккордикс',
              alignment: 'right',
              style: 'titleIcon',
              margin: [0, 20, 40, 0]
            }
          ]
        },
        info: {
          title: `Отчет о прохождении сессии ${this.training.id}`
        },
        content: [
          {
            text: 'Заданные параметры',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: '#5F5F61',
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                text: [
                  `Врач: ${this.getSurnameWithInitials(this.training.course.doctor)}\n\n`,
                  `Тип тренировки: Стандартный \n`
                ]
              },
              {
                text: [`Дата: ${this.datePlan}\n\n`, `Границы ЧСС:  ${this.intervalHR}\n`]
              },
              {
                text: [`Длительность: ${this.adjustedDurationStr}\n\n`]
              }
            ]
          },
          `\n`,
          {
            text: `Задание: ${this.exercise}`
          },
          '\n\n',
          {
            text: 'Результаты',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: '#5F5F61',
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                text: [
                  `Начало: ${this.startTime}\n\n`,
                  `Максимальная ЧСС: ${this.maxHeartRate} уд/мин \n\n`,
                  `Средний темп: ${this.averageStepRate}\n\n`
                ]
              },
              {
                text: [
                  `Окончание: ${this.endTime} \n\n`,
                  `Средняя ЧСС: ${this.averageHeartRate}  уд/мин\n\n`,
                  `Кол-во шагов: ${this.stepCountStr}\n\n`
                ]
              },
              {
                text: [
                  `Продолжительность: ${this.trainingDurationString} \n\n`,
                  `Нахождение в зоне ЧСС: ${this.hrInZonePercent}%\n\n`,
                  `Шкала Борга: ${this.borgScaleStr} \n`
                ]
              }
            ]
          },
          '\n',
          {
            text: `Основной ритм: ${this.basicRhythm}`
          },
          '\n',
          {
            text: `Диагноз: ${this.diagnosisStr}`
          },
          '\n',
          {
            alignment: 'justify',
            columns: [
              {
                stack: [
                  {
                    text: 'Жалобы пациента во время и после сессии:',
                    style: 'boldMargin'
                  },
                  {
                    canvas: [
                      {
                        lineColor: '#5F5F61',
                        type: 'line',
                        x1: 0,
                        y1: 0,
                        x2: 188,
                        y2: 0,
                        lineWidth: 1.5
                      }
                    ]
                  },
                  `\n`,
                  {
                    text: [
                      {
                        text: `${this.patientCommentStr}`
                      }
                    ]
                  }
                ]
              }
            ]
          },
          '\n'
        ],
        defaultStyle: {
          fontSize: 9
        },
        styles: {
          header: {
            fontSize: 15
          },
          title: {
            fontSize: 10,
            bold: true,
            margin: [0, 0, 0, 3]
          },
          titleIcon: {
            fontSize: 14,
            bold: true
          },
          boldMargin: {
            bold: true,
            margin: [0, 0, 0, 2]
          },
          marginRow: {
            margin: [3, 0, 3, 0]
          },
          bold: {
            bold: true
          },
          italics: {
            fontSize: 12,
            bold: true,
            italics: true
          },
          table: {
            margin: [0, 7, 5, 15]
          },
          tableHeader: {
            bold: true,
            margin: [5, 5, 5, 5]
          },
          marginRowLarge: {
            margin: [5, 8, 5, 0]
          }
        }
      }
      let chart = this.$parent.$parent.$parent.$refs.trainingResult.$refs.trainingChart
        ? this.$parent.$parent.$parent.$refs.trainingResult.$refs.trainingChart.chart
        : null
      if (chart && chart.data.length) {
        chart.series.values.forEach(series => {
          const dashArrayMap = {
            hrSeries: null,
            breathSeries: '8,4',
            stepSeries: '8,4,2,4'
          }

          if (dashArrayMap.hasOwnProperty(series.id)) {
            series.strokeDasharray = dashArrayMap[series.id]
          }
        })
        let imgChart = await chart.exporting.getImage('jpg')
        chart.series.values.forEach(series => {
          series.strokeDasharray = null
        })
        docDefinition.content.push({
          stack: [
            {
              text: 'Сводный график',
              style: 'boldMargin'
            },
            {
              canvas: [
                {
                  lineColor: '#5F5F61',
                  type: 'line',
                  x1: 0,
                  y1: 0,
                  x2: 75,
                  y2: 0,
                  lineWidth: 1.5
                }
              ]
            },
            `\n\n`,
            {
              image: imgChart,
              fit: [500, 300]
            },
            `\n\n`
          ],
          unbreakable: true
        })
      }
      if (this.eventsList.length) {
        let res = [
          {
            text: 'Зарегистрированные кардиособытия',
            style: 'title'
          },
          {
            margin: [0, 0, 0, 7],
            canvas: [
              {
                lineColor: '#5F5F61',
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 1.5
              }
            ]
          },
          {
            alignment: 'justify',
            columns: [
              {
                stack: [
                  {
                    style: 'table',
                    table: {
                      heights: 13,
                      body: this.tableForRegistredEvents.firstColumn
                    },
                    layout: {
                      fillColor: function(rowIndex) {
                        return rowIndex % 2 === 0 ? colorGreen : null
                      },
                      defaultBorder: false
                    }
                  }
                ]
              }
            ]
          }
        ]
        if (this.tableForRegistredEvents.secondColumn.length > 1) {
          res[2].columns.push({
            stack: [
              {
                style: 'table',
                table: {
                  heights: 13,
                  body: this.tableForRegistredEvents.secondColumn
                },
                layout: {
                  fillColor: function(rowIndex) {
                    return rowIndex % 2 === 0 ? colorGreen : null
                  },
                  defaultBorder: false
                }
              }
            ]
          })
        }
        docDefinition.content.push(...res)
      }
      docDefinition.content.push(
        {
          text: 'Заключение',
          style: 'title'
        },
        {
          margin: [0, 0, 0, 7],
          canvas: [
            {
              lineColor: '#5F5F61',
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 515,
              y2: 0,
              lineWidth: 1.5
            }
          ]
        },
        {
          text: `Во время выполнения сессии ${this.conclusionStr}`
        }
      )
      if (window.navigator.pdfViewerEnabled) {
        pdfMake.createPdf(docDefinition).open()
      } else {
        pdfMake.createPdf(docDefinition).download()
      }
    },
    getEventByTime(event, minute) {
      return event.filter(item => {
        let start = this.markers ? minute * 60 + +this.markers.start : minute * 60
        let end = this.markers ? (minute - 1) * 60 + +this.markers.start : minute * 60
        return item.begin <= start && item.begin >= end
      })
    },
    addEventsToList() {
      this.eventsList = this.events.reduce((list, current) => {
        if (!notShow.includes(current.type) && (!current.end || current.begin <= current.end)) {
          let idx = list.findIndex(item => item.abbr === eventsConfig[current.type].abbr)
          if (idx == -1) {
            list.push(eventsConfig[current.type])
            list[list.length - 1].count = 1
            list[list.length - 1].type = current.type
          } else {
            list[idx].count += 1
          }
        }
        return list
      }, [])
    }
  }
}
</script>
